import React, { Fragment, useState } from "react"
import { styled } from "@mui/material/styles"
import MuiAppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import MenuIcon from "@mui/icons-material/Menu"
import AccountCircle from "@mui/icons-material/AccountCircle"
import IconButton from "@mui/material/IconButton"
import CircularProgress from "@mui/material/CircularProgress"
import InfoOutlined from "@mui/icons-material/InfoOutlined"
import { Box, Divider, Typography, Modal, MenuItem, Menu } from "@mui/material"
import { useKeycloak } from "../auth/keycloakContext"
import { Link } from "react-router-dom"

const drawerWidth = 400

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Account = () => {
  const [anchor, setAnchor] = React.useState(null)
  const isMenuOpen = Boolean(anchor)
  const keycloak = useKeycloak()

  const handleProfileMenuOpen = (event) => {
    setAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchor(null)
  }

  const logout = async () => {
    try {
      await keycloak.logout() // optional: pass redirectUri
    } catch (err) {
      console.error("Logout failed", err)
    } finally {
      handleMenuClose()
    }
  }

  const username =
    keycloak.tokenParsed?.preferred_username ||
    keycloak.tokenParsed?.email ||
    keycloak.tokenParsed?.sub // fallback to user ID

  return (
    <Fragment>
      <IconButton edge="end" onClick={handleProfileMenuOpen} color="inherit">
        <AccountCircle />
      </IconButton>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        keepMounted
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem>
          <Typography variant="caption">{username}</Typography>
        </MenuItem>
        <MenuItem onClick={logout}>
          <Typography style={{ float: "right" }} color="red" variant="caption">
            Logout
          </Typography>
        </MenuItem>
      </Menu>
    </Fragment>
  )
}

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}))

const UserGuide = () => {
  const [open, set_open] = useState(false)

  return (
    <Fragment>
      <IconButton
        edge="end"
        onClick={() => {
          set_open(true)
        }}
        color="inherit"
      >
        <InfoOutlined />
      </IconButton>
      <Modal
        open={open}
        onClose={() => {
          set_open(false)
        }}
        sx={{ boxShadow: "none" }}
      >
        <Box
          sx={{
            width: 700,
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            mx: "auto",
            mt: "5vh",
          }}
        >
          <Typography variant="h4" gutterBottom>
            User Guide
          </Typography>
          {/* Welcome */}
          <Typography variant="h5" gutterBottom>
            Welcome
          </Typography>
          <Typography>
            Welcome to the platform! This environment is designed for learning
            and exploration. You can experiment freely with different tools and
            settings to understand how everything works.
          </Typography>
          <Typography>
            Because this is a <b>sandbox environment</b> , any changes you make
            are temporary. Nothing will be permanently saved unless you
            explicitly choose to commit your changes. So don`t worry about
            making mistakes — it`s safe to explore!
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Sessions */}
          <Typography variant="h5" gutterBottom>
            Sessions
          </Typography>
          <Typography>
            A <b>Session</b> is your personal workspace where you can experiment
            with planning changes without affecting the main dataset. Changes
            you make in a session are saved separately until they are reviewed
            and approved.
          </Typography>
          <Typography>
            When creating a session, you will need to provide:
          </Typography>
          <ul>
            <li>
              A <b>Name</b> – this helps you and others identify the purpose of
              the session.
            </li>
            <li>
              A <b>Description</b> – a short explanation of what you are trying
              to explore or change.
            </li>
            <li>
              A <b>Geography type</b> – for example Country, Province, District,
              or CUP.
            </li>
            <li>
              A <b>Region</b> – the specific area you want to work in.
            </li>
            <li>
              A <b>Buffer distance</b> – this adds an additional area around
              your selected region so nearby features can also be included in
              the analysis.
            </li>
          </ul>
          <Typography>
            Once a session is created, you can open it from the sessions table
            and begin editing CUPs and boundaries within that study area.
          </Typography>
          <Typography>
            Sessions can also be <b>shared with other users</b>. This allows
            collaborators or approvers to review your work and help evaluate
            proposed changes.
          </Typography>
          <Typography>
            If a session is no longer needed, it can be deleted. Note that
            sessions with submitted changes cannot be deleted until those
            changes are resolved.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Side Navigation */}
          <Typography variant="h5" gutterBottom>
            Side Navigation
          </Typography>
          <Typography>
            The <b>side navigation panel</b> helps you move between different
            parts of the system. To open it, click the small <b>menu icon</b> in
            the top-left corner of your screen.
          </Typography>
          <Typography>
            From here, you can easily access all main functions — such as
            loading a study area, running allocations, adjusting visualisation
            options, and exporting your work.
          </Typography>
          <Typography>
            You can close the panel again if you need more space on your screen.
            Everything can still be reached from this single menu.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Allocate */}
          <Typography variant="h5" gutterBottom>
            Allocate
          </Typography>
          <Typography>
            The <b>Allocate</b> tool matches populations within your study area
            to facilities like clinics or hospitals. This helps estimate how
            many people each facility serves, and where service gaps exist.
          </Typography>
          <Typography>
            To run an allocation, you`ll be asked to select a few settings:{" "}
          </Typography>
          <ul>
            <li>
              <b>Population type</b> - which group of people you`re analyzing.
            </li>
            <li>
              <b>Distance constraint</b> - how far people are expected to travel
              to reach a facility.
            </li>
            <li>
              <b>Facility types</b> - the types of facilities to include (for
              example, hospitals, clinics, or contracted GPs).
            </li>
          </ul>
          <Typography>
            Once you run the allocation, the system calculates results that
            appear in other sections like <b>Interactive Mapping</b> and{" "}
            <b>Visualisation</b> . It adds information such as:{" "}
          </Typography>
          <ul>
            <li>
              <b>For analysis units:</b> Served, Unserved, and Catchment
              populations.
            </li>
            <li>
              <b>For facilities:</b> Utilised and Unutilised values, showing how
              busy or underused each facility is.
            </li>
          </ul>
          <Divider sx={{ my: 2 }} />
          {/* Visualisation */}
          <Typography variant="h5" gutterBottom>
            Visualisation
          </Typography>
          <Typography>
            The <b>Visualisation</b> section lets you control what`s displayed
            on the map and how it looks. This helps you understand your data at
            a glance.
          </Typography>
          <Typography>You can change: </Typography>
          <ul>
            <li>
              <b>Render property:</b> Choose what data (for example, unserved
              population) is used to color the map.
            </li>
            <li>
              <b>Facility dot radius:</b> Adjust how large the facility icons
              appear.
            </li>
            <li>
              <b>Analysis unit opacity:</b> Make the shaded regions more or less
              transparent.
            </li>
            <li>
              <b>Points and Boundaries:</b> Turn on or off features like
              facilities, CUPs, or administrative borders.
            </li>
            <li>
              <b>Miscellaneous tools:</b> Use Reset Map to return to the default
              view or Find Facility to locate a specific facility by name.
            </li>
          </ul>
          <Divider sx={{ my: 2 }} />
          {/* Interactive Mapping */}
          <Typography variant="h5" gutterBottom>
            Interactive Mapping
          </Typography>
          <Typography>
            The map is fully interactive, so you can zoom in and out, move
            around, and click on features to view more details.
          </Typography>
          <Typography>
            If you <b>right-click</b> on a feature, you`ll see options such
            as:{" "}
          </Typography>
          <ul>
            <li>
              <b>Facilities:</b> View, Edit, Delete, or Move them.
            </li>
            <li>
              <b>Analysis Units:</b> Create a new facility, add a CUP, measure
              distances, or reassign populations.
            </li>
            <li>
              <b>Districts:</b> View region details.
            </li>
            <li>
              <b>CUPs:</b> View, Edit, Delete, or Move (click the point, not the
              boundary line).
            </li>
          </ul>
          <Divider sx={{ my: 2 }} />
          {/* Cups */}
          <Typography variant="h5" gutterBottom>
            Cups
          </Typography>
          <Typography>
            The <b>Cups</b> tool helps you find potential new facility locations
            based on population needs. You can use the built-in optimizer to
            automatically generate suitable sites.
          </Typography>
          <Typography>Enter your criteria: </Typography>
          <ul>
            <li>Distance constraint (maximum travel distance)</li>
            <li>Number of cups to generate</li>
            <li>Cup size or capacity</li>
            <li>Population type</li>
          </ul>
          Then click the button to create new CUP points.
          <Typography>
            You can also click on the new CUP points to generate their service
            area boundaries.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Stats */}
          <Typography variant="h5" gutterBottom>
            Stats
          </Typography>
          <Typography>
            The <b>Stats</b> section gives you a quick summary of your results
            once an allocation has been completed. It shows:
          </Typography>
          <ul>
            <li>How much of the population is served vs. unserved</li>
            <li>
              The number of people within different distance bands (e.g. under 5
              km, 10 km, etc.)
            </li>
          </ul>
          <Typography>
            This helps you understand accessibility and identify underserved
            regions.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Export */}
          <Typography variant="h5" gutterBottom>
            Export
          </Typography>
          <Typography>
            Use the <b>Export</b> function to download your results for use in
            other software. You can export map layers and data into standard GIS
            formats like GeoJSON.
          </Typography>
          <Typography>
            This allows you to continue your analysis in other programs or share
            your results with others.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Change Requests */}
          <Typography variant="h5" gutterBottom>
            Change Requests
          </Typography>
          <Typography>
            It is possible to make changes to the session that are not
            immediately applied to the base data. This allows you to experiment
            with adjustments and only submit them for approval once you are
            satisfied with the results.
          </Typography>
          <ul>
            <li>
              You can adjust CUPs and their boundaries on the map until the
              result looks correct.
            </li>
            <li>
              Right-click to save your edit. The change will be stored in your
              session and will load automatically the next time you open the
              tool, layered on top of the base data.
            </li>
            <li>
              When you are ready, go to “Changes” in the side navigation and
              submit the change for approval.
            </li>
            <li>
              You can share your session with an approver, or request that the
              system administrator grant you the approver role.
            </li>
            <li>
              Once a change is accepted, it will be merged into the main dataset
              and become visible to all users.
            </li>
          </ul>
          <Divider sx={{ my: 2 }} />
          {/* General Workflow */}
          <Typography variant="h5" gutterBottom>
            General Workflow
          </Typography>
          <Typography>
            1. Load a study area (for example, a CUP with a 50 km buffer).
          </Typography>
          <Typography>
            2. Run an allocation to link populations to facilities.
          </Typography>
          <Typography>
            3. Open the Visualisation panel and view unserved areas on the map.
          </Typography>
          <Typography>
            4. Identify where there are gaps in service coverage.
          </Typography>
          <Typography>
            5. Right-click on a CUP point and select <b>View</b> to open CUP
            profiling.
          </Typography>
          <Typography>
            6. Add a new facility in an underserved area or change an existing
            facility`s type to improve access.
          </Typography>
          <Typography>
            7. Rerun the allocation to update your results and include the new
            facility types.
          </Typography>
          <Typography>
            8. Compare the new CUP profiling data and updated unserved maps.
          </Typography>
          <Typography>
            9. Export your results or save the session for later.
          </Typography>
          <Typography>
            10. When finished, close the browser tab safely.
          </Typography>
        </Box>
      </Modal>
    </Fragment>
  )
}

export default function Header({
  open,
  dispatch_ui,
  loading,
  collapsible = false,
}) {
  return (
    <Fragment>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {collapsible ? (
            <IconButton
              color="inherit"
              onClick={() => {
                dispatch_ui({ type: "OPEN_DRAWER" })
              }}
              sx={{ mr: 2, ...(open && { display: "none" }) }}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Typography
            component={Link}
            to="/"
            variant="h6"
            noWrap
            sx={{
              color: "white",
              textDecoration: "none",
            }}
          >
            Spatial Planning Tool
          </Typography>

          {loading ? (
            <CircularProgress
              color="secondary"
              style={{
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          ) : (
            <div
              style={{
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          )}
          <UserGuide />
          <Account />
        </Toolbar>
      </AppBar>
      <DrawerHeader />
    </Fragment>
  )
}
